import { useCallback, useEffect, useRef, useState } from 'react';
import { createQrReader } from './qr';

// Module 19 (BL-380) G5 - the door scanner's camera. The camera starts only when staff press "Start camera" (browsers require a tap, and
// nobody is filmed by surprise), uses the back camera when there is one, reads about five frames a second, and stops when the screen closes.
// state: 'idle' | 'starting' | 'running' | 'denied' | 'unsupported' | 'error'
export default function useQrScanner({ onCode, win = (typeof window !== 'undefined' ? window : undefined), readerFactory = createQrReader, intervalMs = 200 } = {}) {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const timerRef = useRef(null);
    const readerRef = useRef(null);
    const onCodeRef = useRef(onCode);
    const [state, setState] = useState('idle');
    const [engine, setEngine] = useState('');
    useEffect(() => { onCodeRef.current = onCode; }, [onCode]);

    const stop = useCallback(() => {
        if (timerRef.current) { win.clearTimeout(timerRef.current); timerRef.current = null; }
        if (streamRef.current) { streamRef.current.getTracks().forEach((track) => track.stop()); streamRef.current = null; }
        if (videoRef.current) videoRef.current.srcObject = null;
        setState((current) => (current === 'running' || current === 'starting' ? 'idle' : current));
    }, [win]);

    const start = useCallback(async () => {
        const media = win?.navigator?.mediaDevices;
        if (!media || typeof media.getUserMedia !== 'function') { setState('unsupported'); return; }
        setState('starting');
        try {
            const stream = await media.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
            streamRef.current = stream;
            const video = videoRef.current;
            if (video) {
                video.srcObject = stream;
                video.setAttribute('playsinline', 'true');
                video.muted = true;
                try { await video.play(); } catch { /* autoplay rules: the stream still shows once the element is visible */ }
            }
            readerRef.current = await readerFactory(win);
            setEngine(readerRef.current.engine);
            setState('running');
            const tick = async () => {
                if (!streamRef.current) return;
                try {
                    const code = await readerRef.current.read(videoRef.current);
                    if (code) onCodeRef.current?.(code);
                } catch {
                    // one unreadable frame is normal; keep going
                }
                if (streamRef.current) timerRef.current = win.setTimeout(tick, intervalMs);
            };
            timerRef.current = win.setTimeout(tick, intervalMs);
        } catch (error) {
            stop();
            setState(error && (error.name === 'NotAllowedError' || error.name === 'SecurityError') ? 'denied' : (error && error.name === 'NotFoundError' ? 'unsupported' : 'error'));
        }
    }, [win, readerFactory, intervalMs, stop]);

    useEffect(() => () => {
        if (timerRef.current) win?.clearTimeout(timerRef.current);
        if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
    }, [win]);

    return { videoRef, state, engine, start, stop };
}

export const CAMERA_MESSAGES = {
    idle: 'Press “Start camera”, then hold a guest’s QR pass inside the frame.',
    starting: 'Starting the camera…',
    running: 'Hold the QR pass inside the frame.',
    denied: 'The camera is blocked for this site. Allow the camera in the browser settings, or type or scan the pass code below.',
    unsupported: 'This device has no camera the browser can use here. Type or scan the pass code below, or check guests in from the list.',
    error: 'The camera could not start. Try again, or type or scan the pass code below.',
};
